import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Panel, PanelTitle, Note } from './ui';
import { systemNames } from '../../config/systems';

/* Open issues: what someone has reported and nobody has closed yet, worst first.
   Issues are kept out of "Needs you" (that list is what the sensors and logs say);
   this card is what people said. Data comes from FarmStatusContext, which refetches
   on every `issue_updated` push. */

const SHOWN = 5;
const SEV_RANK = { high: 0, medium: 1, low: 2 };
const SEV_LABEL = { high: 'High', medium: 'Medium', low: 'Low' };

const sevColor = (theme, sev) => (
  sev === 'high' ? theme.colors.danger : sev === 'medium' ? theme.colors.warning : theme.colors.offline
);

const ago = (ts, now) => {
  const d = Math.floor((now - ts) / 86400);
  if (d >= 1) return d === 1 ? '1 day' : `${d} days`;
  const h = Math.floor((now - ts) / 3600);
  return h >= 1 ? `${h} h` : 'just now';
};

const Rows = styled.ul`
  list-style: none; margin: 0; padding: 0; display: grid;
`;

const Row = styled.li`
  & + & { border-top: 1px solid ${p => p.theme.colors.border}; }
  a {
    display: grid; grid-template-columns: 8px minmax(0, 1fr) auto; gap: 12px; align-items: baseline;
    padding: 10px 0; color: inherit; text-decoration: none; min-height: 0;
  }
  a:hover b { text-decoration: underline; }
  a:focus-visible { outline: 2px solid ${p => p.theme.colors.primary}; outline-offset: 2px; border-radius: 6px; }
  .dot { width: 8px; height: 8px; border-radius: 50%; background: ${p => sevColor(p.theme, p.$sev)}; transform: translateY(-1px); }
  b { display: block; font-weight: 500; line-height: 1.3; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .meta { display: block; margin-top: 2px; font-size: 12px; color: ${p => p.theme.colors.textMuted}; }
  .age { font-size: 12px; color: ${p => p.theme.colors.textMuted}; white-space: nowrap; }
`;

const Count = styled.span`
  font-size: 12px; font-weight: 500; color: ${p => p.theme.colors.textMuted};
  b { font-weight: 500; color: ${p => p.theme.colors.critical}; }
`;

const Foot = styled.div`
  margin-top: 8px; display: flex; justify-content: space-between; gap: 12px;
  a { font-size: 12px; font-weight: 500; color: ${p => p.theme.colors.primary}; text-decoration: none; min-height: 0; padding: 4px 0; }
  a:hover { text-decoration: underline; }
`;

export default function OpenIssuesCard({ issues, now, order }) {
  if (!issues) return null;   // not loaded (or the request failed): say nothing rather than "none open"
  const sorted = [...issues].sort((a, b) => (SEV_RANK[a.severity] - SEV_RANK[b.severity]) || (b.updated_at - a.updated_at));
  const high = issues.filter(i => i.severity === 'high').length;
  const shown = sorted.slice(0, SHOWN);

  return (
    <Panel $order={order}>
      <PanelTitle>
        <span>Open issues</span>
        {issues.length > 0 && (
          <Count>{high > 0 && <><b>{high} high</b>{issues.length > high ? ', ' : ''}</>}{issues.length > high && `${issues.length - high} more`}</Count>
        )}
      </PanelTitle>
      {issues.length === 0 ? (
        <Note>Nothing reported. Spot a problem? <Link to="/issues/new">Open an issue</Link></Note>
      ) : (
        <>
          <Rows>
            {shown.map(i => (
              <Row key={i.id} $sev={i.severity}>
                <Link to={`/issues/${i.id}`} title={`${SEV_LABEL[i.severity]} severity`}>
                  <span className="dot" aria-hidden />
                  <span style={{ minWidth: 0 }}>
                    <b>{i.title}</b>
                    <span className="meta">
                      {[i.unit_id && systemNames(i.unit_id),
                        i.plant_ids.length > 0 && i.plant_ids.slice(0, 3).join(', ') + (i.plant_ids.length > 3 ? ` +${i.plant_ids.length - 3}` : ''),
                        i.status === 'in_progress' ? 'in progress' : 'open'].filter(Boolean).join(' · ')}
                    </span>
                  </span>
                  <span className="age">{ago(i.created_at, now)}</span>
                </Link>
              </Row>
            ))}
          </Rows>
          <Foot>
            <Link to="/issues">{issues.length > SHOWN ? `All ${issues.length} open issues` : 'All issues'}</Link>
            <Link to="/issues/new">Report a problem</Link>
          </Foot>
        </>
      )}
    </Panel>
  );
}
